import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/domain/Bits";
import { EventItem, EventShape } from "../../components/domain/EventList";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Label } from "../../components/ui/label";
import { toastError } from "../../lib/api";
import { useEffectiveRole } from "../../lib/roles";

type StatusFilter = "all" | EventShape["status"];

type WorkflowConfig = {
  version: number;
  terminology: Array<{ code: string; label: string }>;
  stages: Array<{ code: string; label: string; guidance?: string }>;
  outcomes: Array<{ code: string; label: string }>;
  required_fields: Record<string, string[]>;
  require_visit_verification: boolean;
};

const statuses: StatusFilter[] = [
  "all", "scheduled", "pending_verification", "completed", "rejected",
  "cancelled", "no_show", "rescheduled",
];

function csvCell(value: unknown): string {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

export default function VisitVerification() {
  const [items, setItems] = useState<EventShape[]>([]);
  const role = useEffectiveRole();
  const canManagePolicy = role === "owner_admin";
  const [status, setStatus] = useState<StatusFilter>("pending_verification");
  const [query, setQuery] = useState("");
  const [fromDateTime, setFromDateTime] = useState("");
  const [toDateTime, setToDateTime] = useState("");
  const [workflow, setWorkflow] = useState<WorkflowConfig | null>(null);
  const [policyBusy, setPolicyBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/crm/events", { params: { limit: 500 } });
      setItems(data.items || []);
    } catch {
      setError("Could not load visit records.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canManagePolicy) return;
    api.get<WorkflowConfig>("/tenant/workflow-editor")
      .then(({ data }) => setWorkflow(data))
      .catch((err) => toastError(err, "Could not load the visit verification policy."));
  }, [canManagePolicy]);

  const setVerificationPolicy = async (enabled: boolean) => {
    if (!workflow || policyBusy) return;
    setPolicyBusy(true);
    try {
      const payload = {
        version: workflow.version,
        terminology: workflow.terminology.map(({ code, label }) => ({ code, label })),
        stages: workflow.stages.map(({ code, label, guidance }) => ({ code, label, guidance: guidance || "" })),
        outcomes: workflow.outcomes.map(({ code, label }) => ({ code, label })),
        required_fields: workflow.required_fields,
        require_visit_verification: enabled,
      };
      const { data } = await api.patch<WorkflowConfig>("/tenant/workflow-editor", payload);
      setWorkflow(data);
      toast.success(enabled ? "Visit verification is now required." : "Visit verification is now off.");
      await load();
    } catch (err) {
      toastError(err, "Could not update the visit verification policy.");
    } finally {
      setPolicyBusy(false);
    }
  };

  const visits = useMemo(() => items.filter((item) =>
    item.event_type_code === "home_visit" || item.event_type_code.includes("visit")
  ), [items]);

  const filtered = useMemo(() => visits.filter((item) => {
    if (status !== "all" && item.status !== status) return false;
    const scheduled = item.start_at || item.start_date;
    const scheduledTime = scheduled ? new Date(scheduled).getTime() : Number.NaN;
    if (fromDateTime && (!Number.isFinite(scheduledTime) || scheduledTime < new Date(fromDateTime).getTime())) return false;
    if (toDateTime && (!Number.isFinite(scheduledTime) || scheduledTime > new Date(toDateTime).getTime())) return false;
    const haystack = `${item.title} ${item.location || ""} ${item.completion_outcome || ""}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  }), [visits, status, query, fromDateTime, toDateTime]);

  const counts = useMemo(() => ({
    planned: visits.filter((v) => v.status === "scheduled").length,
    pending: visits.filter((v) => v.status === "pending_verification").length,
    verified: visits.filter((v) => v.status === "completed").length,
    rejected: visits.filter((v) => v.status === "rejected").length,
  }), [visits]);

  function exportCsv() {
    const headers = ["Visit", "Status", "Scheduled start", "Actual verified completion", "Location",
      "Contact ID", "Clinic ID", "Assigned user ID", "Outcome", "Evidence links"];
    const rows = filtered.map((v: any) => [
      v.title, v.status, v.start_at || v.start_date, v.verified_at || v.completed_at,
      v.location, v.contact_id, v.organization_id, v.assigned_to_user_id,
      v.completion_outcome, (v.completion_evidence_links || []).join(" "),
    ]);
    const csv = [headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n");
    const href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = href; link.download = `visit-verification-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click(); URL.revokeObjectURL(href);
  }

  return (
    <>
      <PageHeader title="Visit Verification" subtitle="Separate planned activity from visits that were actually completed and approved" />
      <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-5">
        {canManagePolicy && (
          <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="visit-verification-policy">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-semibold text-slate-900">Visit verification requirement</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {workflow?.require_visit_verification
                    ? "On: completed visits wait for an authorized reviewer before reimbursement."
                    : "Off: staff-completed visits qualify immediately without a second approval."}
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={Boolean(workflow?.require_visit_verification)}
                  disabled={!workflow || policyBusy}
                  onChange={(e) => setVerificationPolicy(e.target.checked)}
                  data-testid="visit-verification-policy-toggle" />
                Require verification
              </label>
            </div>
          </section>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ["Planned", counts.planned, "scheduled"],
            ["Pending verification", counts.pending, "pending_verification"],
            ["Verified completed", counts.verified, "completed"],
            ["Rejected", counts.rejected, "rejected"],
          ].map(([label, value, target]) => (
            <button key={String(label)} onClick={() => setStatus(target as StatusFilter)}
              className="text-left rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-400">
              <div className="text-xs text-slate-500">{label}</div>
              <div className="text-2xl font-semibold text-slate-900">{value}</div>
            </button>
          ))}
        </div>
        <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 lg:grid-cols-[1fr_210px_220px_220px_auto] lg:items-end">
          <div>
            <Label htmlFor="visit-search">Search</Label>
            <Input id="visit-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Visits, outcomes, or locations" className="mt-1" data-testid="visit-search" />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
              <SelectTrigger className="mt-1 w-full" data-testid="visit-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {statuses.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s.replaceAll("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="visit-from">From date and time</Label>
            <Input id="visit-from" type="datetime-local" value={fromDateTime}
              onChange={(e) => setFromDateTime(e.target.value)} className="mt-1" data-testid="visit-from-filter" />
          </div>
          <div>
            <Label htmlFor="visit-to">To date and time</Label>
            <Input id="visit-to" type="datetime-local" value={toDateTime}
              min={fromDateTime || undefined} onChange={(e) => setToDateTime(e.target.value)}
              className="mt-1" data-testid="visit-to-filter" />
          </div>
          <Button variant="outline" onClick={exportCsv} disabled={!filtered.length}>
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading visits…</div>
        ) : error ? (
          <div role="alert" className="text-sm text-red-600">{error} <Button variant="outline" size="sm" onClick={load}>Retry</Button></div>
        ) : filtered.length ? (
          <div className="space-y-2" data-testid="visit-verification-list">
            {filtered.map((event) => <EventItem key={event.id} event={event} onRefetch={load} />)}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
            No visits match these filters.
          </div>
        )}
      </div>
    </>
  );
}
